// Static mirror of the payments Worker's booking catalog.
//
// The Worker (payments-worker/src/booking-catalog.ts) is the source of truth:
// IT decides prices and durations at checkout, never the browser. This mirror
// exists so the wizard renders instantly and — when VITE_PAYMENTS_API is unset
// or the Worker is unreachable — the page can degrade to the WhatsApp flow
// with the chosen treatment prefilled instead of dead-ending.
// Keep the two files in sync when the catalog changes.

export type BookingCategory = "massaggi" | "epilazione" | "rituali" | "skin-care";

export interface BookingOptionDef {
  id: string;
  minutes: number;
  priceEur: number;
  label: string;
  labelEn: string;
}

export interface BookableServiceDef {
  id: string;
  name: string;
  nameEn: string;
  category: BookingCategory;
  options: BookingOptionDef[];
}

const min = (minutes: number, priceEur: number): BookingOptionDef => ({
  id: String(minutes),
  minutes,
  priceEur,
  label: `${minutes} minuti`,
  labelEn: `${minutes} minutes`,
});

export const BOOKING_SERVICES: BookableServiceDef[] = [
  { id: "massaggio-svedese", name: "Massaggio Svedese", nameEn: "Swedish Massage", category: "massaggi", options: [min(60, 60), min(90, 90)] },
  { id: "massaggio-decontratturante", name: "Massaggio Decontratturante", nameEn: "Deep-Tissue Massage", category: "massaggi", options: [min(60, 60), min(90, 90)] },
  { id: "massaggio-linfodrenante", name: "Massaggio Linfodrenante", nameEn: "Lymphatic Drainage Massage", category: "massaggi", options: [min(60, 60)] },
  { id: "massaggio-emozionale", name: "Massaggio Emozionale", nameEn: "Emotional Massage", category: "massaggi", options: [min(60, 60)] },
  { id: "massaggio-anticellulite", name: "Massaggio Anticellulite", nameEn: "Anti-Cellulite Massage", category: "massaggi", options: [min(60, 60)] },
  { id: "massaggio-californiano", name: "Massaggio Californiano", nameEn: "Californian Massage", category: "massaggi", options: [min(90, 90)] },
  { id: "massaggio-ayurvedico", name: "Massaggio Ayurvedico", nameEn: "Ayurvedic Massage", category: "massaggi", options: [min(90, 90)] },
  { id: "massaggio-hawaiano", name: "Massaggio Hawaiano (Lomi Lomi)", nameEn: "Hawaiian Massage (Lomi Lomi)", category: "massaggi", options: [min(60, 70)] },
  { id: "aromaterapia", name: "Massaggio Aromaterapico", nameEn: "Aromatherapy Massage", category: "massaggi", options: [min(60, 60)] },

  { id: "epilazione-braccia", name: "Epilazione a Cera — Braccia", nameEn: "Arm Waxing", category: "epilazione", options: [min(30, 25)] },
  { id: "epilazione-schiena", name: "Epilazione a Cera — Schiena", nameEn: "Back Waxing", category: "epilazione", options: [min(30, 30)] },
  { id: "epilazione-gamba-completa", name: "Epilazione a Cera — Gamba Completa", nameEn: "Full Leg Waxing", category: "epilazione", options: [min(30, 40)] },
  { id: "epilazione-brasiliana-donna", name: "Epilazione Brasiliana — Donna", nameEn: "Brazilian Waxing — Women", category: "epilazione", options: [min(45, 50)] },
  { id: "epilazione-brasiliana-uomo", name: "Epilazione Brasiliana — Uomo", nameEn: "Brazilian Waxing — Men", category: "epilazione", options: [min(90, 120)] },

  { id: "rituale-relax-completo", name: "Relax Completo (esfoliazione + massaggio)", nameEn: "Complete Relax (exfoliation + massage)", category: "rituali", options: [min(90, 110)] },
  { id: "rituale-detox-corpo", name: "Detox Corpo", nameEn: "Body Detox", category: "rituali", options: [min(90, 100)] },
  { id: "rituale-deep-relax", name: "Deep Relax Experience", nameEn: "Deep Relax Experience", category: "rituali", options: [min(120, 160)] },
  { id: "rituale-dream", name: "Dream Ritual (viso-corpo)", nameEn: "Dream Ritual (face & body)", category: "rituali", options: [min(120, 160)] },

  { id: "scrub-body", name: "Scrub Body (esfoliazione completa)", nameEn: "Body Scrub (full-body exfoliation)", category: "skin-care", options: [min(45, 50)] },
];

export const CATEGORY_LABELS: Record<BookingCategory, { it: string; en: string }> = {
  massaggi: { it: "Massaggi", en: "Massages" },
  epilazione: { it: "Epilazione a cera calda", en: "Hot-wax epilation" },
  rituali: { it: "Rituali", en: "Rituals" },
  "skin-care": { it: "Skin care", en: "Skin care" },
};

export function findBookingService(id: string): BookableServiceDef | undefined {
  return BOOKING_SERVICES.find((s) => s.id === id);
}

/**
 * Resolve a `?treatment=` deep-link parameter — either a bookable service id
 * or a site page slug (service landing pages link here with their own slug).
 */
export function resolveServiceId(param: string | null): string | null {
  if (!param) return null;
  if (findBookingService(param)) return param;
  const bySlug: Record<string, string> = {
    "epilazione-brasiliana": "epilazione-brasiliana-donna",
    "epilazione-cera-calda": "epilazione-gamba-completa",
    "skin-care": "scrub-body",
  };
  return bySlug[param] ?? null;
}
